import { ReactNode } from 'react';
import styled, { css } from 'styled-components';

import CloseButton from 'components/shared/CloseButton';
import Foldout from 'components/shared/Foldout';
import useIsMobile from 'hooks/useIsMobile';
import { colors } from 'styles/theme/colors';
import { FONT_WEIGHT_MEDIUM } from 'styles/theme/textStyles';
import ArrowLeft from 'svg/ArrowLeft';

// A right-hand slideout shell for inbound-referral detail views. Built on the shared Foldout, it
// owns the header chrome — back arrow, title, optional subtitle, an optional right-aligned action
// slot, and a close × — and a scrolling body. Section-specific content is passed as children.
//
// One header shape, used by every detail slideout: the back arrow rides the title's own line and
// the subtitle sits beneath it at the header's left edge rather than indented under the title.

// The header's title line. Arrow, title and close × all share this box so the three align
// optically against each other rather than against the taller title+subtitle block — centring the
// × on that block is what had it landing halfway between the title and the subtitle.
const TITLE_LINE_HEIGHT = '24px';

interface Props {
  open: boolean;
  onClose: () => void;
  title: string;
  // Sits inline after the title — a status pill, e.g. the SNF slideout's "Selected: Vista Meadows".
  // headerAction is the right-aligned slot; this reads as part of the title.
  titleBadge?: ReactNode;
  subtitle?: string;
  // Right-aligned header slot (e.g. a Download button). Sits left of the close ×.
  headerAction?: ReactNode;
  // The back arrow duplicates the close × — both just close — so surfaces whose design draws
  // only the × can drop it.
  hideBackButton?: boolean;
  // Fixed under the header, above the scrolling body: the coverage tab strip. Outside the body on
  // purpose — switching coverage has to stay reachable however far down a long result the reader
  // has scrolled.
  tabs?: ReactNode;
  // Hides the header's own border-bottom — for callers whose tabs (or detailsBar) already draw the
  // separator immediately below them, which otherwise doubles up against this one.
  hideHeaderBorder?: boolean;
  // Fixed under the tabs, above the scrolling body: a row of detail fields tied to whatever the tabs
  // are currently showing (eligibility's Payer Name / Member ID). Outside the body for the same
  // reason as tabs — and drawn as its own bordered strip so it reads as scoped to the tab above it
  // rather than as part of the header.
  detailsBar?: ReactNode;
  // Pinned to the bottom, for actions that belong to whatever the body is showing (when it last ran,
  // Download PDF, Re-Run). Same reason as the tabs: a benefits result is long enough that a footer
  // inside the scroll would only be found by reaching the end of it.
  footer?: ReactNode;
  // Overrides the shell's width for a caller whose content needs a different one. Detail slideouts
  // share a width by default so they read as one surface.
  width?: string;
  children: ReactNode;
}

export default function DetailSlideout({
  open,
  onClose,
  title,
  titleBadge,
  subtitle,
  headerAction,
  hideBackButton,
  tabs,
  hideHeaderBorder,
  detailsBar,
  footer,
  width,
  children,
}: Props) {
  const isMobile = useIsMobile();
  // With a subtitle the rule belongs under it, so the header reads as one block with a single rule
  // under all of it rather than one splitting it.
  const hasSubtitle = subtitle !== undefined;

  return (
    <Foldout open={open} onBackdropClick={onClose} width={isMobile ? '100%' : (width ?? 'min(95%, 1040px)')}>
      <Container>
        <HeaderBar $hasSubtitle={hasSubtitle} $hideBorder={hideHeaderBorder}>
          <TitleSection data-testid='slideout-title-row'>
            {!hideBackButton && (
              <BackButton type='button' aria-label='Back' onClick={onClose}>
                <ArrowLeft />
              </BackButton>
            )}
            <Title>{title}</Title>
            {titleBadge}
          </TitleSection>
          <HeaderActions>
            {headerAction}
            <HeaderClose onClick={onClose} />
          </HeaderActions>
        </HeaderBar>
        {hasSubtitle && <Subtitle $hideBorder={hideHeaderBorder}>{subtitle}</Subtitle>}
        {tabs && <TabBar data-testid='slideout-tabs'>{tabs}</TabBar>}
        {detailsBar && <DetailsBar data-testid='slideout-details-bar'>{detailsBar}</DetailsBar>}
        <Body>{children}</Body>
        {footer && <FooterBar data-testid='slideout-footer'>{footer}</FooterBar>}
      </Container>
    </Foldout>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

// With a subtitle below, the header drops its rule and its bottom padding — the subtitle carries
// both. $hideBorder drops the rule for callers whose tabs or detailsBar already draw one below.
const HeaderBar = styled.div<{ $hasSubtitle: boolean; $hideBorder?: boolean }>`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: ${({ $hasSubtitle }) => ($hasSubtitle ? '16px 24px 0' : '16px 24px')};
  flex-shrink: 0;
  ${({ $hasSubtitle, $hideBorder }) =>
    !$hasSubtitle &&
    !$hideBorder &&
    css`
      border-bottom: 1px solid ${colors.black10};
    `}
`;

const TitleSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const Title = styled.h2`
  font-size: 18px;
  line-height: ${TITLE_LINE_HEIGHT};
  font-weight: ${FONT_WEIGHT_MEDIUM};
  margin: 0;
  color: ${colors.black100};
`;

// No horizontal padding, so the arrow's glyph starts at the header's left edge and the subtitle
// below lines up under it rather than 8px inside it.
const BackButton = styled.button`
  display: flex;
  align-items: center;
  height: ${TITLE_LINE_HEIGHT};
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  flex-shrink: 0;
  color: ${colors.black100};
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  min-height: ${TITLE_LINE_HEIGHT};
  flex-shrink: 0;
`;

// The shared close, boxed to the title's line so it sits level with the arrow and the title rather
// than centring on the taller title+subtitle block.
const HeaderClose = styled(CloseButton)`
  height: ${TITLE_LINE_HEIGHT};
`;

// A sibling of the header row rather than a child of the title column, so it starts at the header's
// left edge (flush under the back arrow) instead of indented under the title. It also owns the
// header's bottom rule.
const Subtitle = styled.span<{ $hideBorder?: boolean }>`
  display: block;
  font-size: 14px;
  font-weight: 400;
  color: ${colors.black50};
  padding: 12px 24px 16px;
  flex-shrink: 0;
  ${({ $hideBorder }) =>
    !$hideBorder &&
    css`
      border-bottom: 1px solid ${colors.black10};
    `}
`;

const TabBar = styled.div`
  padding: 0 24px;
  border-bottom: 1px solid ${colors.black10};
  flex-shrink: 0;
`;

const DetailsBar = styled.div`
  padding: 16px 24px;
  border-bottom: 1px solid ${colors.black10};
  flex-shrink: 0;
`;

const FooterBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 24px;
  border-top: 1px solid ${colors.black10};
  background: ${colors.white};
  flex-shrink: 0;
`;

const Body = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;

  /* Sections keep their natural height so the body scrolls to fit. Without this, a child with
     overflow:hidden gets min-height:0 and the flex column shrinks/clips it to ~0 height. */
  > * {
    flex-shrink: 0;
  }
`;
